import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'context/AuthContext';
import { canAction } from 'permissions';
import useEscapeClose from 'hooks/useEscapeClose';

const API = '/api';

const TYPES_SITE = ['bureau', 'entrepot', 'usine', 'banque', 'residence', 'commerce', 'chantier', 'ambassade', 'autre'];
const NIVEAUX_RISQUE = ['faible', 'moyen', 'eleve', 'critique'];

// Togo bounding box
const TOGO_BOUNDS = { latMin: 6.0, latMax: 11.5, lngMin: -0.2, lngMax: 1.9 };

function validateCoords(lat, lng) {
  if (!lat && !lng) return null; // empty is ok
  const la = parseFloat(lat), lo = parseFloat(lng);
  if (isNaN(la) || isNaN(lo)) return 'Coordonnees invalides';
  if (la < TOGO_BOUNDS.latMin || la > TOGO_BOUNDS.latMax || lo < TOGO_BOUNDS.lngMin || lo > TOGO_BOUNDS.lngMax) {
    return 'Ces coordonnees semblent incorrectes. Verifiez l\'adresse ou placez le marqueur manuellement.';
  }
  return null;
}

const EMPTY_FORM = {
  nom: '', adresse: '', ville: '', client_id: '', type_site: 'bureau',
  niveau_risque: 'moyen', instructions: '', latitude: '', longitude: '', rayon_geofence_m: 100,
};

export default function Sites() {
  const { token, user } = useAuth();
  const roles = user?.roles || [];
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEscapeClose(showForm, () => { setShowForm(false); setEditId(null); });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const [sitesRes, clientsRes, statsRes] = await Promise.all([
        fetch(`${API}/sites?search=${search}`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }),
        fetch(`${API}/clients`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }).catch(() => null),
        fetch(`${API}/sites/stats`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }).catch(() => null),
      ]);
      if (sitesRes.ok) setSites(await sitesRes.json());
      if (clientsRes?.ok) setClients(await clientsRes.json());
      if (statsRes?.ok) setStats(await statsRes.json());
    } catch {}
  }, [search, token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({
      nom: s.nom, adresse: s.adresse || '', ville: s.ville || '', client_id: s.client_id || '',
      type_site: s.type_site || 'bureau', niveau_risque: s.niveau_risque || 'moyen',
      instructions: s.instructions || '', latitude: s.latitude || '', longitude: s.longitude || '',
      rayon_geofence_m: s.rayon_geofence_m || 100,
    });
    setShowForm(true);
  };

  const [coordError, setCoordError] = useState('');
  const [geocoding, setGeocoding] = useState(false);

  const geocodeAddress = async () => {
    if (!form.adresse && !form.ville) return alert('Entrez une adresse ou une ville');
    setGeocoding(true);
    try {
      const q = encodeURIComponent(`${form.adresse || ''} ${form.ville || 'Lome'} Togo`.trim());
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, { headers: { 'User-Agent': 'GSS360/1.0' } });
      const data = await res.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat).toFixed(6);
        const lng = parseFloat(data[0].lon).toFixed(6);
        setForm(f => ({ ...f, latitude: lat, longitude: lng }));
        setCoordError('');
      } else {
        alert('Adresse non trouvee. Entrez les coordonnees manuellement.');
      }
    } catch { alert('Erreur de geocodage'); }
    setGeocoding(false);
  };

  const submit = async () => {
    if (!form.nom) return alert('Nom requis');
    const err = validateCoords(form.latitude, form.longitude);
    if (err) { setCoordError(err); return; }
    setCoordError('');
    setLoading(true);
    try {
      const url = editId ? `${API}/sites/${editId}` : `${API}/sites`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST', headers, body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowForm(false);
        setEditId(null);
        setForm(EMPTY_FORM);
        load();
      } else {
        const err = await res.json();
        alert(err.error || 'Erreur');
      }
    } catch { alert('Erreur de connexion'); }
    setLoading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Desactiver ce site ?')) return;
    try {
      await fetch(`${API}/sites/${id}`, { method: 'DELETE', headers });
      load();
    } catch {}
  };

  const risqueColor = (n) => {
    const colors = { faible: '#22C55E', moyen: '#F59E0B', eleve: '#EF4444', critique: '#991B1B' };
    return colors[n] || '#6B7280';
  };

  return (
    <div className="page-container">
<div className="stats-cards">
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Total sites</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div></div><div className="stat-value">{stats.total || 0}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Sites actifs</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div></div><div className="stat-value">{stats.actifs || 0}</div></div>
      </div>

      <div className="section-toolbar">
        <input className="gss-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {canAction(roles, 'sites', 'create') && sites.some(s => !s.code_militaire) && (
            <button className="gss-btn secondary" onClick={async () => {
              try {
                const res = await fetch(`${API}/sites/generate-codes`, { method: 'POST', headers });
                if (res.ok) { const d = await res.json(); alert(d.message); load(); }
              } catch {}
            }}>G&eacute;n&eacute;rer codes OTAN</button>
          )}
          {canAction(roles, 'sites', 'create') && (
            <button className="gss-btn primary" onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY_FORM); }}>+ Nouveau site</button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => { setShowForm(false); setEditId(null); }}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  {editId ? 'Modifier le site' : 'Configuration du Site'}
                </h2>
                <div className="gss-modal-subtitle">Interface de deploiement securise</div>
              </div>
              <button className="gss-modal-close" onClick={() => { setShowForm(false); setEditId(null); }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem 1.75rem' }}>

              {/* Section 1 — Identification */}
              <div className="gss-modal-section">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
                <span>Identification du site</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field"><label>Nom du site *</label><input className="gss-input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex: Site Alpha-7" /></div>
                <div className="gss-field">
                  <label>Client</label>
                  <select className="gss-input" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}>
                    <option value="">-- Aucun --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.raison_sociale} — [{c.statut_commercial}]</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Type de site</label>
                  <select className="gss-input" value={form.type_site} onChange={e => setForm(f => ({ ...f, type_site: e.target.value }))}>
                    {TYPES_SITE.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Niveau de risque</label>
                  <select className="gss-input" value={form.niveau_risque} onChange={e => setForm(f => ({ ...f, niveau_risque: e.target.value }))}
                    style={{ borderLeft: `4px solid ${risqueColor(form.niveau_risque)}` }}>
                    {NIVEAUX_RISQUE.map(n => <option key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              {/* Section 2 — Localisation */}
              <div className="gss-modal-section">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>Localisation & Geosecurite</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field"><label>Adresse</label><input className="gss-input" value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} /></div>
                <div className="gss-field"><label>Ville</label><input className="gss-input" value={form.ville} onChange={e => setForm(f => ({ ...f, ville: e.target.value }))} /></div>
                <div className="gss-field">
                  <label>Position GPS</label>
                  {!form.latitude && !form.longitude ? (
                    <div style={{ background: '#FFF7ED', border: '1px solid #FB923C', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#9A3412', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>&#x26A0;</span> Position a definir
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#22C55E', fontWeight: 600 }}>
                      {parseFloat(form.latitude).toFixed(4)}, {parseFloat(form.longitude).toFixed(4)}
                    </div>
                  )}
                </div>
                <div className="gss-field">
                  <label>&nbsp;</label>
                  <button className="gss-btn secondary" type="button" onClick={geocodeAddress} disabled={geocoding} style={{ width: '100%' }}>
                    {geocoding ? 'Recherche...' : 'Rechercher position depuis adresse'}
                  </button>
                </div>
                <div className="gss-field"><label>Latitude</label><input className="gss-input" type="number" step="any" value={form.latitude} onChange={e => { setForm(f => ({ ...f, latitude: e.target.value })); setCoordError(''); }} placeholder="6.1725" /></div>
                <div className="gss-field"><label>Longitude</label><input className="gss-input" type="number" step="any" value={form.longitude} onChange={e => { setForm(f => ({ ...f, longitude: e.target.value })); setCoordError(''); }} placeholder="1.2314" /></div>
                {coordError && (
                  <div style={{ gridColumn: '1/-1', background: '#FEF2F2', border: '1px solid #EF4444', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991B1B' }}>
                    {coordError}
                  </div>
                )}
                <div className="gss-field">
                  <label>Zone GPS autorisee (m)</label>
                  <input className="gss-input" type="number" value={form.rayon_geofence_m} onChange={e => setForm(f => ({ ...f, rayon_geofence_m: parseInt(e.target.value) || 100 }))} />
                  <div style={{ fontSize: 10, color: '#AAA', marginTop: 4 }}>Distance maximale autour du site ou les agents sont consideres presents</div>
                </div>
              </div>

              {/* Section 3 — Consignes */}
              <div className="gss-modal-section">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span>Consignes de securite</span>
              </div>
              <textarea
                className="gss-textarea-premium"
                value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                placeholder="Instructions speciales pour les agents : rondes obligatoires, zones interdites, procedures..."
                rows={4}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ fontSize: 10, color: '#BBB' }}>{(form.instructions || '').length} caracteres</span>
              </div>

              {/* Section 4 — Statut en direct */}
              <div className="gss-modal-section">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                <span>Statut en direct</span>
              </div>
              <div className="gss-modal-status-bar">
                <div className="gss-modal-status-item"><span className="dot-green" /><span>Site</span><span className="val-green">En ligne</span></div>
                <div className="gss-modal-status-item"><span className="dot-green" /><span>Monitoring</span><span className="val-green">Actif</span></div>
                <div className="gss-modal-status-item"><span>Couverture</span><span className="val">0 agents</span></div>
                <div className="gss-modal-status-item"><span>Menace</span><span className="val-green" style={{ color: risqueColor(form.niveau_risque) }}>{form.niveau_risque || 'Moyen'}</span></div>
                <div className="gss-modal-status-item"><span>Incidents</span><span className="val">0</span></div>
              </div>

            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => { setShowForm(false); setEditId(null); }}>Annuler</button>
              <button className="gss-btn-deploy" onClick={submit} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>
                {loading ? 'Deploiement...' : editId ? 'Mettre a jour' : 'Deployer le site'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="gss-table-wrapper">
        <table className="gss-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Client</th>
              <th>Adresse</th>
              <th>Type</th>
              <th>Risque</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 && <tr><td colSpan="8" style={{ padding: 0, border: 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun site enregistre</div>
                <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
              </div>
            </td></tr>}
            {sites.map(s => (
              <tr key={s.id}>
                <td><span style={{ background: '#1A1A2E', color: '#F59E0B', padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: 12, letterSpacing: 1, whiteSpace: 'nowrap' }}>{s.code_militaire || '—'}</span></td>
                <td><strong>{s.nom}</strong></td>
                <td>{s.client_nom || '-'}</td>
                <td>{s.adresse || '-'}{s.ville ? `, ${s.ville}` : ''}</td>
                <td>{s.type_site}</td>
                <td><span style={{ color: risqueColor(s.niveau_risque), fontWeight: 600 }}>{s.niveau_risque}</span></td>
                <td>
                  <span className={`gss-badge ${s.actif ? 'success' : 'danger'}`}>{s.actif ? 'Actif' : 'Inactif'}</span>
                  {s.actif && !s.latitude && !s.longitude && (
                    <span style={{ display: 'inline-block', marginLeft: 4, background: '#FFF7ED', color: '#9A3412', fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4, border: '1px solid #FB923C' }}>GPS</span>
                  )}
                </td>
                <td style={{ display: 'flex', gap: 4 }}>
                  {canAction(roles, 'sites', 'update') && (
                    <button className="gss-btn-icon" onClick={() => startEdit(s)} title="Modifier">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  )}
                  {canAction(roles, 'sites', 'delete') && (
                    <button className="gss-btn-icon" onClick={() => remove(s.id)} title="Desactiver" style={{ color: '#EF4444' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
